import { stableStringify } from '@provision/formatting-utils';
import { useCallback, useMemo } from 'react';
import { isNullish, keys } from 'remeda';
import { useGlobalStore } from 'src/store';
import {
  AllowedRuleSet,
  MatchType,
  PartnerGroupRules,
  UserGroupRules,
} from './group-rules.types';
import { usePartnerGroupRules } from './partner-group-rules';
import { useUserGroupRuleValues } from './user-group-rules';

export type GroupRules = (PartnerGroupRules | UserGroupRules)[];

interface CheckValidityOptions {
  matchType?: MatchType;
}

const isAllowedRuleSet = (value: unknown): value is AllowedRuleSet => {
  return (
    typeof value === 'object' &&
    value !== null &&
    Array.isArray((value as AllowedRuleSet).rules)
  );
};

export function useGroupRules() {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { partnerGroupRules, loading: partnerRulesLoading } =
    usePartnerGroupRules(selectedPartnerId);

  const { userGroupRules } = useUserGroupRuleValues();

  const groupRules = useMemo(() => {
    const result = (
      !isNullish(selectedPartnerId)
        ? {
            ...partnerGroupRules,
          }
        : {}
    ) as Record<PartnerGroupRules | UserGroupRules, boolean | number[]>;

    keys.strict(userGroupRules).forEach((rule) => {
      if (
        userGroupRules[rule] &&
        !(partnerGroupRules[rule as unknown as PartnerGroupRules] === false)
      ) {
        result[rule] = userGroupRules[rule];
      }
      if (userGroupRules[rule] === false) {
        result[rule] = userGroupRules[rule];
      }
    });
    return result;
    // using JSON.stringify makes shallow check making the hook isn't called on reference change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPartnerId, stableStringify({ userGroupRules, partnerGroupRules })]);

  const checkValidity = useCallback(
    (
      allowedRules: (PartnerGroupRules | UserGroupRules | AllowedRuleSet)[],
      { matchType = 'any' }: CheckValidityOptions = {},
    ): boolean => {
      const validateFn =
        matchType === 'all'
          ? allowedRules.every.bind(allowedRules)
          : allowedRules.some.bind(allowedRules);
      return validateFn((rule) => {
        if (isAllowedRuleSet(rule)) {
          return checkValidity(rule.rules, { matchType: rule.matchType ?? 'all' });
        }
        return Boolean(groupRules[rule]);
      });
    },
    [groupRules],
  );

  return {
    groupRules,
    checkValidity,
    loading: partnerRulesLoading,
  };
}
